import React from 'react';

/**
 * AddOnDropdown - Displays half/full add-on items under a menu card
 * @param {Array} addOnItems - Array of add-on items (half/full) for this menu item
 * @param {Function} removeAddOnItem - Callback to remove an add-on item
 * @param {Function} addAddOnItem - Callback to add more of an add-on item
 * @param {Function} toggleComplimentary - Callback to toggle complimentary status
 */
const AddOnDropdown = ({ addOnItems, removeAddOnItem, addAddOnItem, toggleComplimentary }) => {
  if (!addOnItems || addOnItems.length === 0) {
    return null;
  }

  // Separate half and full items
  const halfItem = addOnItems.find(item => item.add_on_type === 'half');
  const fullItem = addOnItems.find(item => item.add_on_type === 'full');

  return (
    <div
      className="absolute left-2.5 right-2.5 top-[142px] bg-white border border-amber-200 rounded-b-xl shadow-lg px-3 py-2 z-50 w-auto"
      style={{ maxWidth: "calc(100% - 20px)" }}
    >
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 pb-1">
          Add-Ons
        </div>

        {/* Half Option */}
        {halfItem && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between rounded-lg px-2 py-1.5 border border-amber-100">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase flex-shrink-0">
                  Half
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs font-medium text-gray-700 truncate">
                    KWD {parseFloat(halfItem.price?.replace('KWD ', '') || 0).toFixed(3)}
                  </span>
                  {halfItem.complimentaryCount > 0 && (
                    <span className="text-[9px] text-green-600 font-semibold truncate">
                      ({halfItem.complimentaryCount} complimentary)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center bg-white rounded-md h-[22px] px-1 border border-amber-200 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAddOnItem(halfItem);
                  }}
                  className="w-6 h-6 rounded flex items-center justify-center text-sm text-amber-600 font-bold"
                >
                  -
                </button>
                <span className="h-4 text-xs w-6 text-center font-semibold text-gray-700">
                  {halfItem.quantity}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addAddOnItem(halfItem);
                  }}
                  className="w-6 h-6 rounded flex items-center justify-center text-sm text-amber-600 font-bold"
                >
                  +
                </button>
              </div>
            </div>
            {toggleComplimentary && halfItem.quantity > 0 && (
              <div className="flex items-center gap-2 px-2">
                <span className="text-[10px] text-gray-600">Complimentary:</span>
                <div className="flex items-center bg-white rounded-md h-[20px] px-1 border border-gray-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newCount = Math.max(0, (halfItem.complimentaryCount || 0) - 1);
                      toggleComplimentary(halfItem, newCount);
                    }}
                    disabled={(halfItem.complimentaryCount || 0) === 0}
                    className="w-5 h-5 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                  >
                    -
                  </button>
                  <span className="h-4 text-xs w-5 text-center font-semibold text-gray-700">
                    {halfItem.complimentaryCount || 0}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newCount = Math.min(halfItem.quantity, (halfItem.complimentaryCount || 0) + 1);
                      toggleComplimentary(halfItem, newCount);
                    }}
                    disabled={(halfItem.complimentaryCount || 0) >= halfItem.quantity}
                    className="w-5 h-5 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Full Option */}
        {fullItem && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between bg-green-50 rounded-lg px-2 py-1.5 border border-green-100">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="bg-green-600 text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase flex-shrink-0">
                  Full
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs font-medium text-gray-700 truncate">
                    KWD {parseFloat(fullItem.price?.replace('KWD ', '') || 0).toFixed(3)}
                  </span>
                  {fullItem.complimentaryCount > 0 && (
                    <span className="text-[9px] text-green-600 font-semibold truncate">
                      ({fullItem.complimentaryCount} complimentary)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center bg-white rounded-md h-[22px] px-1 border border-green-200 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAddOnItem(fullItem);
                  }}
                  className="w-6 h-6 rounded flex items-center justify-center text-sm text-green-600 font-bold"
                >
                  -
                </button>
                <span className="h-4 text-xs w-6 text-center font-semibold text-gray-700">
                  {fullItem.quantity}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addAddOnItem(fullItem);
                  }}
                  className="w-6 h-6 rounded flex items-center justify-center text-sm text-green-600 font-bold"
                >
                  +
                </button>
              </div>
            </div>
            {toggleComplimentary && fullItem.quantity > 0 && (
              <div className="flex items-center gap-2 px-2">
                <span className="text-[10px] text-gray-600">Complimentary:</span>
                <div className="flex items-center bg-white rounded-md h-[20px] px-1 border border-gray-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newCount = Math.max(0, (fullItem.complimentaryCount || 0) - 1);
                      toggleComplimentary(fullItem, newCount);
                    }}
                    disabled={(fullItem.complimentaryCount || 0) === 0}
                    className="w-5 h-5 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                  >
                    -
                  </button>
                  <span className="h-4 text-xs w-5 text-center font-semibold text-gray-700">
                    {fullItem.complimentaryCount || 0}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newCount = Math.min(fullItem.quantity, (fullItem.complimentaryCount || 0) + 1);
                      toggleComplimentary(fullItem, newCount);
                    }}
                    disabled={(fullItem.complimentaryCount || 0) >= fullItem.quantity}
                    className="w-5 h-5 rounded flex items-center justify-center text-xs text-gray-600 font-bold disabled:opacity-30"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOnDropdown;

